import { GiftIcon, ShoppingBagIcon, SparklesIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import { useReferralPayoutCadenceTranslations, useReferralRoleTranslations } from '../../../hooks/translations/useReferralRoleTranslations';
import { T } from '../../../translation/src';
import { ReferralOverviewFieldsFragment, ReferralRole } from '../../../types/graphqlTypes';
import { formatPrice } from '../../../utils/formatPrice';
import { cutPercent, ordersForCustomers } from './partnerRoles';
import { partnerTotals } from './partnerTotals';

interface ReferralHeroProps {
  overview: ReferralOverviewFieldsFragment;
}

// One bar with a segment per milestone, each the same width whatever the distance in sales (10 → 50 → 100
// are far apart in sales, not on the bar): the fill runs to the sales count, interpolated within the segment
// it is in, so the first milestone is not squeezed against the left edge.
const progressPercent = ({ milestones, salesCount }: { milestones: ReferralOverviewFieldsFragment['milestones']; salesCount: number }): number => {
  if (!milestones.length) return 0;
  const segment = 100 / milestones.length;
  let from = 0;
  for (const [index, milestone] of milestones.entries()) {
    if (salesCount < milestone.sales) return segment * index + (segment * (salesCount - from)) / (milestone.sales - from);
    from = milestone.sales;
  }
  return 100;
};

const MilestoneBar: React.FC<{ milestones: ReferralOverviewFieldsFragment['milestones']; salesCount: number }> = ({ milestones, salesCount }) => {
  const percent = useMemo(() => progressPercent({ milestones, salesCount }), [milestones, salesCount]);
  const segment = milestones.length ? 100 / milestones.length : 100;
  return (
    // Padding on the right keeps the last milestone's labels inside the panel.
    <div className="relative mt-9 mb-6 mr-10 ml-1">
      <div className="h-3 w-full overflow-hidden rounded-full bg-white/15">
        <div className="h-full rounded-full bg-gradient-to-r from-neon-green-300 to-neon-green-500  transition-all duration-700" style={{ width: `${percent}%` }} />
      </div>
      {milestones.map((milestone, index) => (
        <div key={milestone.sales} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${segment * (index + 1)}%` }}>
          <div
            className={classNames(
              'flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors',
              milestone.reached ? 'border-neon-green-300 bg-neon-green-500 text-eerie-black' : 'border-white/30 bg-dark-green-gradient-dark text-white/50',
            )}
          >
            <GiftIcon className="h-4 w-4" />
          </div>
          <span className={classNames('absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold', milestone.reached ? 'bg-neon-green-500 text-eerie-black' : 'bg-white/10 text-white/90')}>
            +{milestone.projects} <T _str="projects" />
          </span>
          <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-white/70">
            <T _str="{sales} subscriptions" args={{ sales: milestone.sales }} />
          </span>
        </div>
      ))}
    </div>
  );
};

// A customer's deal, as an equation: every paid subscription is a free project (the milestones come on top).
const ProjectPerSale: React.FC = () => (
  <div className="flex flex-wrap items-center gap-3 self-start rounded-xl border border-white/15 bg-black/20 px-3 py-2 text-sm font-semibold">
    <span className="flex items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10">
        <ShoppingBagIcon className="h-4 w-4 text-white/80" />
      </span>
      <T _str="1 paid subscription" />
    </span>
    <span className="text-lg font-normal text-white/50">=</span>
    <span className="flex items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-neon-green-500 text-eerie-black">
        <GiftIcon className="h-4 w-4" />
      </span>
      <T _str="1 free project" />
    </span>
  </div>
);

const BigNumber: React.FC<{ value: React.ReactNode; label: React.ReactNode }> = ({ value, label }) => (
  <div className="flex flex-col">
    <span className="text-4xl font-bold leading-none tracking-tight">{value}</span>
    <span className="mt-1 text-sm text-white/70">{label}</span>
  </div>
);

// The panel at the top: what the organisation earns, in one glance, and how far it is.
const ReferralHero_: React.FC<ReferralHeroProps> = ({ overview }) => {
  const roles = useReferralRoleTranslations();
  const cadences = useReferralPayoutCadenceTranslations();
  const customer = overview.role === ReferralRole.Customer;
  // A reseller or full-service partner: a discount taken off its invoices, not a commission paid out.
  const orders = ordersForCustomers(overview.role);
  const totals = partnerTotals(overview.sales);
  const first = cutPercent(overview.firstCutBasisPoints);
  const recurring = cutPercent(overview.recurringCutBasisPoints);

  return (
    // A customer's panel is slimmer: its deal and its progress, no totals.
    <div className={classNames('relative overflow-hidden rounded-2xl bg-gradient-to-br from-dark-green-gradient-light via-dark-green-gradient-dark to-eerie-black text-white shadow-md', customer ? 'p-5 sm:p-6' : 'p-6 sm:p-8')}>
      {/* A soft glow in the corner, so the panel is not a flat block of colour. */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-neon-green-400/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-dark-green-400/10 blur-3xl" />
      <div className={classNames('relative flex flex-col', customer ? 'gap-4' : 'gap-6')}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/70">
            <SparklesIcon className="h-4 w-4 text-neon-green-300" />
            {customer ? <T _str="referral program" /> : <T _str="partner program" />}
          </span>
          <span className="rounded-full border border-white/30 bg-white/10 px-3 py-0.5 text-xs font-semibold">{roles[overview.role]}</span>
        </div>
        {/* A customer's deal is shown as an equation instead (ProjectPerSale). */}
        {!customer && (
          <h2 className="max-w-xl text-2xl font-bold leading-snug sm:text-3xl">
            {orders ? (
              first === recurring ? (
                <T _str="Your discount: {cut}% on every order." args={{ cut: first }} />
              ) : (
                <T _str="Your discount: {first}% on a customer's first order, {recurring}% on every order after it." args={{ first, recurring }} />
              )
            ) : overview.recurringCutBasisPoints === 0 ? (
              <T _str="Your commission: {cut}% of a new customer's first sale." args={{ cut: first }} />
            ) : first === recurring ? (
              <T _str="Your commission: {cut}% of every sale." args={{ cut: first }} />
            ) : (
              <T _str="Your commission: {first}% of a customer's first sale, {recurring}% of every sale after it." args={{ first, recurring }} />
            )}
          </h2>
        )}
        {customer ? (
          <>
            <ProjectPerSale />
            <div className="flex flex-col">
              <span className="text-xs font-semibold uppercase tracking-widest text-white/60">
                <T _str="bonus milestones" />
              </span>
              <MilestoneBar milestones={overview.milestones} salesCount={overview.salesCount} />
            </div>
          </>
        ) : (
          <div className="flex flex-wrap gap-10">
            {orders ? (
              <>
                <BigNumber value={formatPrice(totals.discountCents)} label={<T _str="discount in total" />} />
                <BigNumber value={formatPrice(totals.notInvoicedCents)} label={<T _str="not invoiced yet" />} />
                <BigNumber value={formatPrice(totals.awaitingPaymentCents)} label={<T _str="invoiced, awaiting payment" />} />
              </>
            ) : (
              <>
                <BigNumber value={formatPrice(overview.earnedCents)} label={<T _str="commission in total" />} />
                <BigNumber value={formatPrice(overview.unpaidCents)} label={<T _str="not paid out yet" />} />
                <BigNumber value={cadences[overview.payoutCadence]} label={<T _str="commission paid out, from {minimum}" args={{ minimum: formatPrice(overview.minPayoutCents) }} />} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export const ReferralHero = memo(ReferralHero_);
